import React from "react";
import Select from "react-select";
import { useGetEmployeesQuery } from "../../masters/employees/employeesApi";
import { useCanSearchEmployees } from "../useTechnicalPermissions";

// Multi-select of the technicians assignable to a work item.
//
// Filtered by DEPARTMENT, not designation - `departmentNameLike=TECHNICAL`,
// exactly what Flutter's TechnicianLookupApi sends. That was the user's own
// correction when this was first built (designation-name-LIKE-'%TECHNICIAN%'
// was tried first): the picker must not offer the whole company.
//
// A plain react-select over the whole fetched list rather than an AsyncSelect
// like EmployeePicker: the TECHNICAL department is ~25 people per branch, so
// one branch-scoped fetch beats a search-as-you-type round trip, and a
// manager assigning work wants to SEE who is available rather than have to
// already know the name.
const toOption = (emp) => ({
  value: emp.recordGuid,
  label: `${emp.empName || ""} ${emp.empLastName || ""}`.trim() + ` · ${emp.empCode}`,
  employee: emp,
});

// Said when there is no branch to fetch against yet. Every other caller hands the
// picker a work item that already has a branch, so the default names the branch -
// but LogWorkModal derives the branch from the CUSTOMER, and telling that user to
// "choose a branch first" sent them to the header's branch filter, which this
// picker never reads. Whoever supplies the branch gets to word the prompt.
const TechnicianPicker = ({
  branchId,
  value,
  onChange,
  isDisabled = false,
  menuPortalTarget,
  noBranchLabel = "Choose a branch first",
}) => {
  const canSearch = useCanSearchEmployees();

  const { data: employees = [], isLoading, isError } = useGetEmployeesQuery(
    { branchId: branchId ?? undefined, departmentNameLike: "TECHNICAL", isActivate: "true" },
    { skip: !canSearch }
  );

  // Reached only by an account holding none of the permissions that imply
  // picking a technician - i.e. one that has no business assigning work at
  // all. Say so plainly rather than showing an empty dropdown, which would
  // read as "this branch has no technicians".
  if (!canSearch) {
    return (
      <div className="alert alert-warning mb-0 py-2 px-3">
        <small>This account cannot assign technicians.</small>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="alert alert-danger mb-0 py-2 px-3">
        <small>Could not load the technician list.</small>
      </div>
    );
  }

  return (
    <>
      <Select
        classNamePrefix="react-select"
        isMulti
        isLoading={isLoading}
        isDisabled={isDisabled}
        options={employees.map(toOption)}
        value={value}
        onChange={onChange}
        placeholder={branchId ? "Choose technician(s)..." : noBranchLabel}
        noOptionsMessage={() => "No active employees in the TECHNICAL department for this branch"}
        menuPortalTarget={menuPortalTarget}
        styles={menuPortalTarget ? { menuPortal: (base) => ({ ...base, zIndex: 9999 }) } : undefined}
      />
      <small className="text-muted">Staff in the TECHNICAL department only.</small>
    </>
  );
};

export default TechnicianPicker;
